/**
 * PagedGrid —— 通用分页表格（docs/ui_page 模板 01 移植，全局一份）
 * 基于 Fluent UI v9 DataGrid 的薄封装：
 *   - 两种选择模式：single（默认，点行进详情，无选择列）/ multiselect（复选框 + 批量删除）
 *   - 列宽可拖拽（TableResizeHandle）+ 一键重置列宽
 *   - 列头可附 tooltip；sortable 列显示只读排序指示（▲/▼ + 优先级）
 *   - 分页（首页 / 上一页 / 页码跳转 / 下一页 / 末页）
 *   - 表格标题栏（title）
 *   - 批量删除确认 Dialog 用 DialogCloseTitle（docs/ui_dialog：右上角 ✕，底部仅动作按钮）
 */
import { ReactNode, useMemo, useState, useCallback, useEffect } from 'react'
import {
  Spinner,
  Button,
  Text,
  Input,
  makeStyles,
  tokens,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  TableResizeHandle,
  InfoLabel,
  createTableColumn,
  type TableColumnId,
  type OnSelectionChangeData,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
} from '@fluentui/react-components'
import {
  ChevronLeft20Regular,
  ChevronRight20Regular,
  ChevronDoubleLeft20Regular,
  ChevronDoubleRight20Regular,
  ArrowReset20Regular,
  Delete20Regular,
} from '@fluentui/react-icons'
import { DialogCloseTitle } from './DialogCloseTitle'

const useStyles = makeStyles({
  empty: {
    padding: '40px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    marginBottom: '4px',
    minHeight: '32px',
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  selectedText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  headerCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  sortIndicator: {
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  gridWrapper: {
    width: '100%',
    overflowX: 'auto',
  },
  cellContent: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: '0px',
  },
  titleBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '8px',
    paddingBottom: '8px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  titleText: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
  },
  pager: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '16px',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },
  pageInfo: {
    marginRight: 'auto',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  pageJump: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  pageInput: {
    width: '56px',
  },
})

/** 排序规则：列 key + 方向（key 必须与后端 sort 白名单列名一致） */
export interface SortRule {
  key: string
  dir: 'asc' | 'desc'
}

export interface PagedColumn<T> {
  key: string
  header: string
  /** 列头说明 tooltip（可选）；设置后列头右侧出现 info 图标 */
  headerTooltip?: string
  width?: string
  /** 是否可排序（仅用于列头只读排序指示；实际排序通过调用方的排序弹窗配置） */
  sortable?: boolean
  render: (row: T) => ReactNode
}

export type PagedSelectionMode = 'single' | 'multiselect'

export interface PagedGridProps<T> {
  rows: T[]
  columns: PagedColumn<T>[]
  loading?: boolean
  rowKey: (row: T) => string
  total: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  /** 表格标题栏文本（位于工具栏上方；一页多表时用于区分各表格数据） */
  title?: ReactNode
  emptyText?: string
  /** 行点击回调（single 模式下点击行进入详情；multiselect 模式下不触发） */
  onRowClick?: (row: T) => void
  /** 是否启用列宽拖拽（默认 true） */
  resizable?: boolean
  /** 选择模式：single（默认，点击行进详情）/ multiselect（复选框 + 批量操作） */
  selectionMode?: PagedSelectionMode
  /** 选中项变化回调（multiselect=true 时有效） */
  onSelectionChange?: (selectedRows: T[]) => void
  /** 批量删除回调（multiselect=true 时有效）；设置后工具栏出现删除按钮 */
  onDelete?: (selectedRows: T[]) => Promise<void> | void
  /** 删除确认对话框标题（默认"确认删除"） */
  deleteDialogTitle?: string
  /** 删除确认对话框内容（默认"确认删除选中的 N 项？此操作不可撤销。"） */
  deleteDialogContent?: (selectedRows: T[]) => ReactNode
  /** 当前排序规则（服务端排序；只读指示，由调用方排序弹窗维护） */
  sort?: SortRule[]
  /** 工具栏左侧额外控件（如「选择模式」开关） */
  toolbarLeft?: ReactNode
  /** 工具栏右侧排序按钮（弹窗入口），渲染在「重置列宽」左侧 */
  sortButton?: ReactNode
}

export function PagedGrid<T>(props: PagedGridProps<T>) {
  const {
    rows,
    columns: columnsDef,
    loading,
    rowKey,
    total,
    page,
    pageSize,
    onPageChange,
    title,
    emptyText,
    onRowClick,
    resizable = true,
    selectionMode = 'single',
    onSelectionChange,
    onDelete,
    deleteDialogTitle = '确认删除',
    deleteDialogContent,
    sort = [],
    toolbarLeft,
    sortButton,
  } = props
  const styles = useStyles()
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const isMulti = selectionMode === 'multiselect'

  // 页码跳转输入框的本地状态（与当前页同步）
  const [jumpValue, setJumpValue] = useState(String(page))
  useEffect(() => { setJumpValue(String(page)) }, [page])
  const jumpTo = () => {
    const n = parseInt(jumpValue, 10)
    if (Number.isNaN(n)) { setJumpValue(String(page)); return }
    const target = Math.min(Math.max(n, 1), totalPages)
    if (target !== page) onPageChange(target)
    else setJumpValue(String(page))
  }

  // ===== 选中状态（仅 multiselect） =====
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const selectedRows = useMemo(
    () => rows.filter((r) => selectedKeys.has(rowKey(r))),
    [rows, selectedKeys, rowKey],
  )

  const handleSelectionChange = useCallback(
    (_e: React.MouseEvent | React.KeyboardEvent, data: OnSelectionChangeData) => {
      const newKeys = new Set<string>()
      data.selectedItems.forEach((id) => newKeys.add(String(id)))
      setSelectedKeys(newKeys)
      const newRows = rows.filter((r) => newKeys.has(rowKey(r)))
      onSelectionChange?.(newRows)
    },
    [rows, rowKey, onSelectionChange],
  )

  // ===== 删除确认 =====
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await onDelete?.(selectedRows)
      setSelectedKeys(new Set())
      onSelectionChange?.([])
    } finally {
      setDeleting(false)
      setDeleteDialogOpen(false)
    }
  }

  // ===== 列定义 =====
  const columns = useMemo(
    () =>
      columnsDef.map((c) =>
        createTableColumn<T>({
          columnId: c.key as TableColumnId,
          renderHeaderCell: () => {
            // sortable 列显示只读排序指示（▲/▼ + 优先级上标），不可点击
            if (c.sortable) {
              const idx = sort.findIndex((s) => s.key === c.key)
              const rule = idx >= 0 ? sort[idx] : undefined
              return (
                <div className={styles.headerCell}>
                  <span>{c.header}</span>
                  {rule && (
                    <span className={styles.sortIndicator}>
                      {rule.dir === 'asc' ? ' ▲' : ' ▼'}
                      {sort.length > 1 && <sup>{idx + 1}</sup>}
                    </span>
                  )}
                  {c.headerTooltip && (
                    <InfoLabel info={c.headerTooltip} size="small" />
                  )}
                </div>
              )
            }
            return (
              <div className={styles.headerCell}>
                {c.header}
                {c.headerTooltip && (
                  <InfoLabel info={c.headerTooltip} size="small" />
                )}
              </div>
            )
          },
          renderCell: (item) => c.render(item),
        }),
      ),
    [columnsDef, styles, sort],
  )

  // ===== 列宽选项（用于初始宽度 + 重置） =====
  const defaultColumnSizing = useMemo(() => {
    const opts: Record<string, { defaultWidth: number }> = {}
    for (const c of columnsDef) {
      const px = c.width ? parseInt(c.width.replace(/px$/, ''), 10) : 150
      if (!Number.isNaN(px)) opts[c.key] = { defaultWidth: px }
    }
    return opts
  }, [columnsDef])

  // 列宽 state（重置时重新设为 defaultColumnSizing）
  const [columnSizing, setColumnSizing] = useState<Record<string, { defaultWidth: number }>>(defaultColumnSizing)
  const [resizeNonce, setResizeNonce] = useState(0)

  const resetColumns = useCallback(() => {
    setColumnSizing({ ...defaultColumnSizing })
    setResizeNonce((n) => n + 1)
  }, [defaultColumnSizing])

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" />
      </div>
    )
  }

  const hasSelection = isMulti && selectedKeys.size > 0

  return (
    <div>
      {/* 表格标题栏（一页多表时区分数据） */}
      {title != null && (
        <div className={styles.titleBar}>
          <Text className={styles.titleText}>{title}</Text>
        </div>
      )}

      {/* 表格工具栏 */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          {toolbarLeft}
          {isMulti && hasSelection && (
            <>
              <Text className={styles.selectedText}>
                已选中 {selectedKeys.size} 项
              </Text>
              {onDelete && (
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Delete20Regular />}
                  style={{ color: tokens.colorPaletteRedForeground1 }}
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  删除
                </Button>
              )}
            </>
          )}
        </div>
        <div className={styles.toolbarRight}>
          {sortButton}
          {resizable && (
            <Button
              appearance="subtle"
              size="small"
              icon={<ArrowReset20Regular />}
              onClick={resetColumns}
            >
              重置列宽
            </Button>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className={styles.empty}><Text>{emptyText ?? '暂无数据'}</Text></div>
      ) : (
        <div className={styles.gridWrapper}>
          <DataGrid
            key={resizeNonce}
            items={rows}
            columns={columns}
            getRowId={rowKey}
            resizableColumns={resizable}
            columnSizingOptions={columnSizing}
            selectionMode={isMulti ? 'multiselect' : undefined}
            onSelectionChange={isMulti ? handleSelectionChange : undefined}
          >
          <DataGridHeader>
            <DataGridRow>
              {(column) => (
                <DataGridHeaderCell className={styles.cellContent}>
                  {column.renderHeaderCell()}
                  {resizable && <TableResizeHandle />}
                </DataGridHeaderCell>
              )}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<T>>
            {({ item }) => (
              <DataGridRow<T>
                onClick={
                  !isMulti && onRowClick ? () => onRowClick(item) : undefined
                }
                style={!isMulti && onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {(column) => (
                  <DataGridCell className={styles.cellContent}>
                    {column.renderCell(item)}
                  </DataGridCell>
                )}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
      </div>
      )}

      <div className={styles.pager}>
        <Text className={styles.pageInfo}>
          共 {total} 条 · 第 {page} / {totalPages} 页
        </Text>
        <Button
          icon={<ChevronDoubleLeft20Regular />}
          disabled={page <= 1}
          onClick={() => onPageChange(1)}
        >
          首页
        </Button>
        <Button
          icon={<ChevronLeft20Regular />}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          上一页
        </Button>
        <div className={styles.pageJump}>
          <Text>跳至</Text>
          <Input
            className={styles.pageInput}
            value={jumpValue}
            onChange={(_, d) => setJumpValue(d.value.replace(/[^0-9]/g, ''))}
            onKeyDown={(e) => { if (e.key === 'Enter') jumpTo() }}
          />
          <Text>页</Text>
          <Button size="small" appearance="subtle" onClick={jumpTo}>跳转</Button>
        </div>
        <Button
          icon={<ChevronRight20Regular />}
          iconPosition="after"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          下一页
        </Button>
        <Button
          icon={<ChevronDoubleRight20Regular />}
          iconPosition="after"
          disabled={page >= totalPages}
          onClick={() => onPageChange(totalPages)}
        >
          末页
        </Button>
      </div>

      {/* 批量删除确认 Dialog（docs/ui_dialog：右上角 ✕，底部仅「确认删除」动作按钮） */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(_, d) => {
          if (!d.open) setDeleteDialogOpen(false)
        }}
        modalType="alert"
      >
        <DialogSurface>
          <DialogBody>
            <DialogCloseTitle title={deleteDialogTitle} onClose={() => setDeleteDialogOpen(false)} />
            <DialogContent>
              {deleteDialogContent
                ? deleteDialogContent(selectedRows)
                : `确认删除选中的 ${selectedKeys.size} 项？此操作不可撤销。`}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                style={{ background: tokens.colorPaletteRedBackground3 }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? '删除中...' : '确认删除'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
